import React, { useMemo } from 'react'
import { Autocomplete, Box, CircularProgress, InputLabel, type SxProps, TextField, type Theme, Typography } from '@mui/material'
import { useFormContext, Controller, type FieldValues, type Control } from 'react-hook-form'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { debounce } from 'lodash'
import { grey } from '../../../colors'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

export interface GenericAutocompleteProps<T> {
  name: string
  label?: string
  options?: T[]
  readOnly?: boolean
  placeholder?: string
  getOptionLabel: (option: T) => any
  getOptionValue?: (option: T) => any
  onChange?: (selectedValue: T | null) => void
  required?: boolean
  loading?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  selectedValue?: string
  onInputChange?: (value: string) => void
  control?: Control<any>
  getOptionDisabled?: (value: T) => any
  sx?: SxProps<Theme>
  wrapperStyle?: SxProps<Theme>
  defaultValue?: any
  clearIcon?: React.ReactNode
  getOptionSubLabel?: (option: T) => string
  textFieldStyles?: SxProps<Theme>
  disableCloseOnSelect?: boolean
  isOptionEqualToValue?: (option: T, value: T) => boolean
  /** Stored value when the user clears the field. Defaults to `null`. */
  clearValue?: unknown
}

const GenericAutocomplete = <T,>({
  name,
  label,
  options,
  readOnly,
  placeholder = 'Type to search…',
  getOptionLabel,
  getOptionValue = () => { },
  onChange,
  required,
  loading,
  fullWidth = true,
  selectedValue,
  size = 'small',
  onInputChange = () => { },
  control,
  getOptionDisabled,
  sx,
  wrapperStyle,
  defaultValue,
  clearIcon,
  getOptionSubLabel,
  textFieldStyles = {},
  disableCloseOnSelect,
  isOptionEqualToValue,
  clearValue = null,
  ...rest
}: GenericAutocompleteProps<T>): JSX.Element => {
  const { control: contextControl } = useFormContext<FieldValues>()

  const getDebouncedInput = useMemo(
    () => debounce(onInputChange, 800),
    [onInputChange]
  )

  return (
    <Controller
      name={name}
      control={control ?? contextControl}
      defaultValue={defaultValue}
      render={({ field: { onChange: onFieldChange, onBlur, value: fieldValue }, fieldState: { error } }) => {
        const value = fieldValue ?? selectedValue
        const errorMessage = error?.message ?? false

        const foundVal = (() => {
          if (options == null) return undefined
          if (isOptionEqualToValue != null && value != null) {
            const matched = options.find((option: T) => isOptionEqualToValue(option, value as T))
            if (matched !== undefined) return matched
          }
          return options.find((option: T) => getOptionValue(option) === value)
        })()

        const defaultRenderOption = (props: any, option: T, { selected }: { selected: boolean }): JSX.Element => {
          const label = getOptionLabel(option)
          const subLabel = getOptionSubLabel?.(option)

          return (
            <li {...props} style={{ width: '100%' }}>

              <Box display="flex" flexDirection="column">
                <Typography fontSize="14px" color={grey[250]} fontWeight={500}>
                  {label}
                </Typography>
                {subLabel && <Typography fontSize="14px" color={grey[850]}>{subLabel}</Typography>}
              </Box>
            </li>
          )
        }

        return (
          <Box minHeight={'72px'} sx={wrapperStyle}>
            {
              label && <InputLabel sx={{ marginBottom: 2 }} required={required}>{label}</InputLabel>
            }

            <Autocomplete
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options
                const lowerInput = inputValue.toLowerCase()
                return options.filter((option: T) => {
                  const label = getOptionLabel(option)?.toString().toLowerCase() ?? ''
                  const subLabel = getOptionSubLabel?.(option)?.toString().toLowerCase() ?? ''
                  return label.includes(lowerInput) || subLabel.includes(lowerInput)
                })
              }}
              disableCloseOnSelect={disableCloseOnSelect}
              id={name}
              blurOnSelect={true}
              clearOnBlur={true}
              clearIcon={clearIcon}
              selectOnFocus={true}
              handleHomeEndKeys={true}
              loading={loading}
              size={size}
              fullWidth={fullWidth}
              value={foundVal ?? null}
              getOptionDisabled={getOptionDisabled}
              options={options ?? []}
              disabled={readOnly}
              popupIcon={<KeyboardArrowDownIcon />}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue ??
                ((option, val) => {
                  if (val == null || option == null) return false
                  return getOptionValue(option) === getOptionValue(val as T)
                })}
              renderOption={defaultRenderOption}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  getDebouncedInput(value)
                }
              }}
              renderInput={({ InputProps, ...params }) => {
                return (
                  <TextField placeholder={placeholder}
                    InputProps={{
                      ...InputProps,
                      startAdornment: (
                        <>
                          <SearchRoundedIcon style={{ color: grey[400] }} />
                          {InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {InputProps.endAdornment}
                        </React.Fragment>
                      ),
                      sx: {
                        ...textFieldStyles
                      }
                    }}
                    {...params}
                    fullWidth={fullWidth}
                    variant="outlined"
                    onBlur={onBlur}
                    helperText={errorMessage}
                    error={Boolean(errorMessage)}
                  />
                )
              }}
              onChange={(event, newValue: T | null) => {
                if (newValue == null) {
                  onFieldChange(clearValue as any)
                } else {
                  onFieldChange(getOptionValue(newValue))
                }
                if (onChange != null) {
                  onChange(newValue)
                }
              }}
              sx={sx}
              {...rest}
            />
          </Box>
        )
      }}
    />
  )
}

export default GenericAutocomplete
